'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TechCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  technologies: string[];
}

export default function TechStack() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const techCategories: TechCategory[] = [
    {
      id: 'cat_frontend',
      name: 'Frontend Development',
      description: 'Modern UI frameworks and libraries',
      icon: 'ComputerDesktopIcon',
      technologies: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Vue.js'],
    },
    {
      id: 'cat_backend',
      name: 'Backend Development',
      description: 'Robust server-side technologies',
      icon: 'ServerIcon',
      technologies: ['Node.js', 'Python', 'Java', 'PostgreSQL', 'MongoDB'],
    },
    {
      id: 'cat_cloud',
      name: 'Cloud & Infrastructure',
      description: 'Scalable cloud solutions',
      icon: 'CloudIcon',
      technologies: ['AWS', 'Azure', 'Google Cloud', 'Terraform', 'CloudFlare'],
    },
    {
      id: 'cat_devops',
      name: 'DevOps & CI/CD',
      description: 'Automated deployment pipelines',
      icon: 'CogIcon',
      technologies: ['Docker', 'Kubernetes', 'Jenkins', 'GitHub Actions', 'GitLab CI'],
    },
    {
      id: 'cat_security',
      name: 'Security & Compliance',
      description: 'Enterprise-grade security',
      icon: 'ShieldCheckIcon',
      technologies: ['SSL/TLS', 'OAuth', 'GDPR', 'ISO 27001', 'Penetration Testing'],
    },
    {
      id: 'cat_data',
      name: 'Data & Analytics',
      description: 'Data-driven insights',
      icon: 'ChartBarIcon',
      technologies: ['Power BI', 'Tableau', 'Python', 'R', 'Apache Spark'],
    },
  ];

  return (
    <section className="py-20 lg:py-32 relative z-10 bg-gray-950">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16 animate-on-scroll animate-in">
          <span className="text-xs uppercase tracking-[0.4em] text-blue-400 mb-4 block">
            Technology Stack
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Powered by <span className="text-gradient-blue">Cutting-Edge Tech</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Leveraging the latest technologies and industry best practices to build robust, scalable solutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {techCategories.map((category, index) => (
            <div
              key={category.id}
              className={`group bg-gray-900 border border-zinc-800 rounded-xl p-8 hover:border-blue-500 transition-all duration-300 animate-on-scroll animate-in-delay-${
                (index % 3) + 1
              }`}
            >
              <div className="w-14 h-14 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 mb-6 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                <Icon name={category.icon as any} size={28} />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-2">{category.name}</h3>
              <p className="text-gray-400 text-sm mb-6">{category.description}</p>
              
              <div className="flex flex-wrap gap-2">
                {category.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-gray-950 border border-zinc-700 rounded-full text-xs text-gray-300 hover:border-blue-500 hover:text-blue-400 transition-colors"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center animate-on-scroll animate-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gray-900 border border-zinc-800 rounded-full">
            <span className="text-gray-400">Ready to leverage these technologies?</span>
            <a
              href="/contact"
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
            >
              Let's Talk →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}